import { Callback } from "game-common/util"
import { Graphics } from "pixi.js"
import { GuiManager } from "../gui_manager"
import { Modal } from "./modal"
import { disposeTextTextures } from "../text/dispose_text"

export interface FullScreenModalProps {
    padding?: number
    opaque?: boolean
    unescapable?: boolean
    // Override the full-screen dark backdrop alpha. Pass 0 for a non-blocking modal that lets the
    // world stay visible behind it (e.g. a docked side panel that only draws its own opaque region).
    backgroundAlpha?: number
}

export abstract class FullScreenModal {
    protected guiManager: GuiManager
    private show: boolean
    modal: Modal
    private surface: Graphics
    private padding: number
    onClose: Callback<boolean>
    private opaque: boolean
    private unescapable: boolean
    private backgroundAlpha?: number

    constructor(guiManager: GuiManager, props?: FullScreenModalProps) {
        this.guiManager = guiManager
        this.show = false
        this.surface = new Graphics()
        this.padding = props?.padding || 10
        this.opaque = props?.opaque || false
        this.unescapable = props?.unescapable || false
        this.backgroundAlpha = props?.backgroundAlpha
    }

    abstract createContent(): Graphics

    isOpen = () => this.show
    update = () => {
        // Free the previous content's per-instance Text textures before rebuilding -- otherwise every
        // open of this modal (inventory, character sheet, etc.) strands all its grid item labels /
        // quantity / stat Text textures. Shared item-sprite textures are left intact.
        ;[...this.surface.children].forEach(disposeTextTextures)
        this.surface.removeChildren()
        this.surface.addChild(this.createContent())
    }

    width = () => this.modal?.width
    height = () => this.modal?.height

    close = () => {
        this.show = !this.show
        if (!this.show && this.modal) {
            this.modal.close()
        }
    }

    toggle = (onClose?: Callback<boolean>) => {
        this.show = !this.show
        if (!this.show && this.modal) {
            this.modal.close()
        }

        if (this.show) {
            this.modal = this.guiManager.createModal({
                padding: this.padding,
                content: this.surface,
                fullScreen: true,
                backgroundAlpha: this.backgroundAlpha !== undefined ? this.backgroundAlpha : !this.opaque ? 0.91 : 1.0,
                plain: true,
                unescapeable: this.unescapable,
                closeCallback: (clicked: boolean) => {
                    this.show = false
                    if (this.onClose) {
                        this.onClose(clicked)
                    } else if (onClose) {
                        onClose(clicked)
                    }
                },
            })

            this.update()
        }
    }
}
